import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronRight, Copy, Plus, Trash2 } from 'lucide-react';
import {
  FORM_QUESTION_LABELS,
  FORM_QUESTION_TYPES,
  formatExpr,
  formId,
  FormulaError,
  parseFormula,
  type FormItem,
  type FormPage,
  type FormQuestion,
  type FormQuestionType,
  type FormSection,
} from '@kaba/shared';
import { Button, cx, DropdownContent, DropdownItem, DropdownMenu, DropdownTrigger, Sheet } from '../../ui/ui';
import { ConditionEditor } from './ConditionEditor';
import { allQuestions, duplicateItem, formulaRefs, locate, moveItem, moveToPage, newQuestion, newSection, refsBefore, removeItem, updateItem, updatePage, type Def } from './model';

const inp = 'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:min-h-9 sm:text-sm';

/** Kennzeichen statt technischer IDs (U01) */
function badges(it: FormItem): string[] {
  const b: string[] = [];
  if (it.kind === 'question') {
    if (it.required) b.push('Pflicht');
    if (it.requiredIf?.items.length) b.push('Bedingt Pflicht');
    if (it.visibleIf?.items.length) b.push('Bedingt');
    if (it.type === 'calc') b.push('Berechnet');
  } else {
    if (it.repeat) b.push('Wiederholbar');
    if (it.visibleIf?.items.length) b.push('Bedingt');
  }
  return b;
}

/** Inhalt: Seiten links, Fragen in der Mitte, Eigenschaften rechts (U02); am Handy je eine Ebene */
export function ContentTab({ def, onChange, selected, onSelect }: { def: Def; onChange: (d: Def) => void; selected: string | null; onSelect: (id: string | null) => void }) {
  const [pageId, setPageId] = useState(def.pages[0]!.id);
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 1023px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const page = def.pages.find((p) => p.id === pageId) ?? def.pages[0]!;
  useEffect(() => {
    // Auswahl auf einer anderen Seite: dorthin wechseln
    const loc = selected ? locate(def, selected) : null;
    if (loc && loc.page.id !== pageId) setPageId(loc.page.id);
  }, [selected, def, pageId]);

  const add = (it: FormItem, sectionId?: string) => {
    if (sectionId) onChange(updateItem(def, sectionId, (s) => ({ ...(s as FormSection), items: [...(s as FormSection).items, it as FormQuestion] })));
    else onChange(updatePage(def, page.id, (p) => ({ ...p, items: [...p.items, it] })));
    onSelect(it.id);
  };
  const sel = selected ? locate(def, selected) : null;

  const props = sel ? (
    <ItemProps def={def} item={sel.item} section={sel.section} onChange={onChange} onDelete={() => (onChange(removeItem(def, sel.item.id)), onSelect(null))} />
  ) : (
    <PageProps def={def} page={page} onChange={onChange} />
  );

  return (
    <div className="grid min-h-0 grid-cols-1 gap-4 lg:grid-cols-[13rem_minmax(0,1fr)_22rem]">
      {/* Seiten: am Handy als Auswahl „Seite 1 von 5: …“, breit als Liste */}
      {narrow ? (
        <nav aria-label="Seiten" className="flex items-center gap-2">
          <select
            aria-label="Seite wählen"
            className="min-h-11 min-w-0 flex-1 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base font-medium"
            value={page.id}
            onChange={(e) => {
              setPageId(e.target.value);
              onSelect(null);
            }}
          >
            {def.pages.map((p, i) => (
              <option key={p.id} value={p.id}>
                Seite {i + 1} von {def.pages.length}: {p.title || 'Ohne Titel'}
                {p.visibleIf?.items.length ? ' (bedingt)' : ''}
              </option>
            ))}
          </select>
          <Button type="button" variant="ghost" className="min-h-11" onClick={() => {
            const p: FormPage = { id: formId('p'), title: `Seite ${def.pages.length + 1}`, items: [] };
            onChange({ ...def, pages: [...def.pages, p] });
            setPageId(p.id);
            onSelect(null);
          }} disabled={def.pages.length >= 15}>
            <Plus size={14} aria-hidden /> Seite
          </Button>
        </nav>
      ) : (
      <nav aria-label="Seiten" className="flex flex-col gap-1">
        <p className="px-1 text-xs font-semibold tracking-wider text-[var(--ink-soft)] uppercase">Seiten</p>
        <ol className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible">
          {def.pages.map((p, i) => (
            <li key={p.id} className="shrink-0">
              <button
                type="button"
                aria-current={p.id === page.id ? 'page' : undefined}
                onClick={() => {
                  setPageId(p.id);
                  onSelect(null);
                }}
                className={cx(
                  'flex min-h-11 w-full items-center gap-2 rounded-lg px-2.5 text-left text-sm sm:min-h-9',
                  p.id === page.id ? 'bg-accent-soft font-semibold' : 'hover:bg-black/[0.04] dark:hover:bg-white/5',
                )}
              >
                <span className="text-xs text-[var(--ink-soft)] tabular-nums">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate">{p.title || 'Ohne Titel'}</span>
                {p.visibleIf?.items.length ? <span className="rounded bg-amber-100 px-1 text-[10px] text-amber-900 dark:bg-amber-900 dark:text-amber-100">Bedingt</span> : null}
              </button>
            </li>
          ))}
        </ol>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            const p: FormPage = { id: formId('p'), title: `Seite ${def.pages.length + 1}`, items: [] };
            onChange({ ...def, pages: [...def.pages, p] });
            setPageId(p.id);
            onSelect(null);
          }}
          disabled={def.pages.length >= 15}
        >
          <Plus size={14} /> Seite
        </Button>
      </nav>
      )}

      {/* Fragen */}
      <section aria-label={`Fragen auf „${page.title || 'Seite'}“`} className="flex min-w-0 flex-col gap-2">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={cx('flex min-h-11 items-center justify-between rounded-lg border px-3 text-left text-sm', !selected ? 'border-accent ring-1 ring-accent' : 'border-[var(--line)]')}
        >
          <span>
            <span className="font-semibold">{page.title || 'Ohne Titel'}</span> <span className="text-[var(--ink-soft)]">· Seite einstellen</span>
          </span>
          <ChevronRight size={16} aria-hidden />
        </button>
        {page.items.length === 0 && <p className="rounded-lg border border-dashed border-[var(--line)] p-4 text-center text-sm text-[var(--ink-soft)]">Diese Seite ist noch leer.</p>}
        <ul className="flex flex-col gap-2">
          {page.items.map((it, i) => (
            <li key={it.id}>
              <ItemRow def={def} item={it} first={i === 0} last={i === page.items.length - 1} selected={selected} onSelect={onSelect} onChange={onChange} page={page} />
              {it.kind === 'section' && (
                <div className="mt-1 ml-4 flex flex-col gap-1 border-l-2 border-[var(--line)] pl-3">
                  {it.items.map((q, j) => (
                    <ItemRow key={q.id} def={def} item={q} first={j === 0} last={j === it.items.length - 1} selected={selected} onSelect={onSelect} onChange={onChange} page={page} />
                  ))}
                  <AddMenu label={`Frage in „${it.title}“`} onAdd={(t) => add(newQuestion(t), it.id)} />
                </div>
              )}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2">
          <AddMenu label="Frage oder Abschnitt" withSections onAdd={(t) => add(newQuestion(t))} onSection={(rep) => add(newSection(rep))} />
        </div>
      </section>

      {/* Eigenschaften */}
      {narrow ? (
        <Sheet open={!!selected} onOpenChange={(o) => !o && onSelect(null)} title="Frage einstellen" widthClass="sm:w-[520px]">
          <div className="p-4">{props}</div>
        </Sheet>
      ) : (
        <aside aria-label="Eigenschaften" className="flex flex-col gap-3 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 lg:sticky lg:top-0 lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto">
          {props}
        </aside>
      )}
      {narrow && !selected && <div className="rounded-xl border border-[var(--line)] p-3">{props}</div>}
    </div>
  );
}

function ItemRow({ def, item, first, last, selected, onSelect, onChange, page }: { def: Def; item: FormItem; first: boolean; last: boolean; selected: string | null; onSelect: (id: string) => void; onChange: (d: Def) => void; page: FormPage }) {
  const title = item.kind === 'section' ? item.title : item.label || 'Ohne Beschriftung';
  const type = item.kind === 'section' ? (item.repeat ? 'Wiederholbarer Abschnitt' : 'Abschnitt') : FORM_QUESTION_LABELS[item.type];
  return (
    <div className={cx('flex items-center gap-1 rounded-lg border bg-[var(--panel)] pr-1', selected === item.id ? 'border-accent ring-1 ring-accent' : 'border-[var(--line)]')}>
      <button type="button" onClick={() => onSelect(item.id)} className="flex min-h-12 min-w-0 flex-1 flex-col items-start justify-center px-3 py-1.5 text-left">
        <span className="w-full truncate text-sm font-medium">{title}</span>
        <span className="flex flex-wrap items-center gap-1 text-xs text-[var(--ink-soft)]">
          {type}
          {badges(item).map((b) => (
            <span key={b} className={cx('rounded px-1', b.startsWith('Bedingt') ? 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100' : b === 'Pflicht' ? 'bg-black/5 dark:bg-white/10' : 'bg-sky-100 text-sky-900 dark:bg-sky-900 dark:text-sky-100')}>
              {b}
            </span>
          ))}
        </span>
      </button>
      <button type="button" className="inline-flex size-10 items-center justify-center rounded-md hover:bg-black/5 disabled:opacity-30 dark:hover:bg-white/10" aria-label={`„${title}“ nach oben`} disabled={first} onClick={() => onChange(moveItem(def, item.id, -1))}>
        <ArrowUp size={15} />
      </button>
      <button type="button" className="inline-flex size-10 items-center justify-center rounded-md hover:bg-black/5 disabled:opacity-30 dark:hover:bg-white/10" aria-label={`„${title}“ nach unten`} disabled={last} onClick={() => onChange(moveItem(def, item.id, 1))}>
        <ArrowDown size={15} />
      </button>
      <DropdownMenu>
        <DropdownTrigger asChild>
          <button type="button" className="inline-flex size-10 items-center justify-center rounded-md hover:bg-black/5 dark:hover:bg-white/10" aria-label={`Weitere Aktionen für „${title}“`}>
            ⋯
          </button>
        </DropdownTrigger>
        <DropdownContent>
          <DropdownItem icon={<Copy size={15} />} onSelect={() => onChange(duplicateItem(def, item.id))}>
            Duplizieren
          </DropdownItem>
          {locate(def, item.id)?.section === null &&
            def.pages
              .filter((p) => p.id !== page.id)
              .map((p) => (
                <DropdownItem key={p.id} icon={<ChevronRight size={15} />} onSelect={() => onChange(moveToPage(def, item.id, p.id))}>
                  Auf „{p.title || 'Seite'}“ verschieben
                </DropdownItem>
              ))}
        </DropdownContent>
      </DropdownMenu>
    </div>
  );
}

function AddMenu({ label, onAdd, withSections, onSection }: { label: string; onAdd: (t: FormQuestionType) => void; withSections?: boolean; onSection?: (repeat: boolean) => void }) {
  return (
    <DropdownMenu>
      <DropdownTrigger asChild>
        <Button type="button" size="sm" variant="ghost" className="self-start">
          <Plus size={14} /> {label}
        </Button>
      </DropdownTrigger>
      <DropdownContent align="start">
        {FORM_QUESTION_TYPES.map((t) => (
          <DropdownItem key={t} onSelect={() => onAdd(t)}>
            {FORM_QUESTION_LABELS[t]}
          </DropdownItem>
        ))}
        {withSections && (
          <>
            <DropdownItem onSelect={() => onSection?.(false)}>Abschnitt</DropdownItem>
            <DropdownItem onSelect={() => onSection?.(true)}>Wiederholbarer Abschnitt (z. B. Positionen)</DropdownItem>
          </>
        )}
      </DropdownContent>
    </DropdownMenu>
  );
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="flex flex-col gap-1 text-sm font-medium">
      {label}
      {children}
      {hint && <span className="text-xs font-normal text-[var(--ink-soft)]">{hint}</span>}
    </label>
  );
}

const num = (v: string) => (v.trim() === '' ? undefined : Number(v.replace(',', '.')));

function PageProps({ def, page, onChange }: { def: Def; page: FormPage; onChange: (d: Def) => void }) {
  const set = (fn: (p: FormPage) => FormPage) => onChange(updatePage(def, page.id, fn));
  const i = def.pages.findIndex((p) => p.id === page.id);
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">Seite {i + 1}</h3>
      <Field label="Titel der Seite">
        <input className={inp} value={page.title} maxLength={200} onChange={(e) => set((p) => ({ ...p, title: e.target.value }))} />
      </Field>
      {i > 0 && (
        <details open={!!page.visibleIf?.items.length} className="text-sm">
          <summary className="cursor-pointer font-medium">Erweitert: Seite nur anzeigen, wenn …</summary>
          <div className="mt-2">
            <ConditionEditor def={def} refs={refsBefore(def, page.id)} value={page.visibleIf} onChange={(c) => set((p) => ({ ...p, visibleIf: c }))} label="Bedingung für diese Seite" />
            <p className="mt-1 text-xs text-[var(--ink-soft)]">Trifft die Bedingung nicht zu, wird die Seite übersprungen. Freie Sprünge gibt es nicht – so bleibt „Zurück“ verlässlich.</p>
          </div>
        </details>
      )}
      <div className="flex flex-wrap gap-2 border-t border-[var(--line)] pt-3">
        <Button type="button" size="sm" disabled={i === 0} onClick={() => onChange({ ...def, pages: swap(def.pages, i, i - 1) })}>
          <ArrowUp size={14} /> Seite nach vorn
        </Button>
        <Button type="button" size="sm" disabled={i === def.pages.length - 1} onClick={() => onChange({ ...def, pages: swap(def.pages, i, i + 1) })}>
          <ArrowDown size={14} /> nach hinten
        </Button>
        {def.pages.length > 1 && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-rose-700 dark:text-rose-300"
            onClick={() => {
              if (page.items.length && !window.confirm(`Seite „${page.title}“ mit ${page.items.length} Fragen löschen?`)) return;
              onChange({ ...def, pages: def.pages.filter((p) => p.id !== page.id) });
            }}
          >
            <Trash2 size={14} /> Seite löschen
          </Button>
        )}
      </div>
    </div>
  );
}

function swap<T>(a: T[], i: number, j: number): T[] {
  const n = [...a];
  [n[i], n[j]] = [n[j]!, n[i]!];
  return n;
}

function ItemProps({ def, item, section, onChange, onDelete }: { def: Def; item: FormItem; section: FormSection | null; onChange: (d: Def) => void; onDelete: () => void }) {
  const set = (fn: (it: FormItem) => FormItem) => onChange(updateItem(def, item.id, fn));
  if (item.kind === 'section') {
    const s = item;
    const setS = (patch: Partial<FormSection>) => set((x) => ({ ...(x as FormSection), ...patch }));
    return (
      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">{s.repeat ? 'Wiederholbarer Abschnitt' : 'Abschnitt'}</h3>
        <Field label="Titel">
          <input className={inp} value={s.title} maxLength={200} onChange={(e) => setS({ title: e.target.value })} />
        </Field>
        <Field label="Hilfetext">
          <textarea className={cx(inp, 'min-h-16 py-2')} value={s.help ?? ''} maxLength={2000} onChange={(e) => setS({ help: e.target.value || undefined })} />
        </Field>
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <input type="checkbox" className="size-5" checked={!!s.repeat} onChange={(e) => setS({ repeat: e.target.checked ? { min: 1, max: 10, itemLabel: 'Eintrag' } : null })} />
          Mehrfach ausfüllbar (z. B. Positionen)
        </label>
        {s.repeat && (
          <div className="grid grid-cols-3 gap-2">
            <Field label="Bezeichnung">
              <input className={inp} value={s.repeat.itemLabel} maxLength={60} onChange={(e) => setS({ repeat: { ...s.repeat!, itemLabel: e.target.value } })} />
            </Field>
            <Field label="Mindestens">
              <input className={inp} inputMode="numeric" value={s.repeat.min} onChange={(e) => setS({ repeat: { ...s.repeat!, min: Math.max(0, Math.min(20, Number(e.target.value) || 0)) } })} />
            </Field>
            <Field label="Höchstens">
              <input className={inp} inputMode="numeric" value={s.repeat.max} onChange={(e) => setS({ repeat: { ...s.repeat!, max: Math.max(1, Math.min(20, Number(e.target.value) || 1)) } })} />
            </Field>
          </div>
        )}
        <details open={!!s.visibleIf?.items.length} className="text-sm">
          <summary className="cursor-pointer font-medium">Erweitert: Abschnitt nur anzeigen, wenn …</summary>
          <div className="mt-2">
            <ConditionEditor def={def} refs={refsBefore(def, s.id)} value={s.visibleIf} onChange={(c) => setS({ visibleIf: c })} label="Bedingung hinzufügen" />
          </div>
        </details>
        <DeleteBtn onDelete={onDelete} what={`Abschnitt „${s.title}“ mit ${s.items.length} Fragen`} />
      </div>
    );
  }
  return <QuestionProps def={def} q={item} section={section} set={(fn) => set((x) => fn(x as FormQuestion))} onDelete={onDelete} />;
}

function DeleteBtn({ onDelete, what }: { onDelete: () => void; what: string }) {
  return (
    <div className="border-t border-[var(--line)] pt-3">
      <Button type="button" size="sm" variant="ghost" className="text-rose-700 dark:text-rose-300" onClick={() => window.confirm(`${what} löschen?`) && onDelete()}>
        <Trash2 size={14} /> Löschen
      </Button>
    </div>
  );
}

function QuestionProps({ def, q, section, set, onDelete }: { def: Def; q: FormQuestion; section: FormSection | null; set: (fn: (q: FormQuestion) => FormQuestion) => void; onDelete: () => void }) {
  const patch = (p: Partial<FormQuestion>) => set((x) => ({ ...x, ...p }));
  const refs = refsBefore(def, q.id);
  const t = q.type;
  return (
    <div className="flex flex-col gap-3">
      <Field label="Frage">
        <input className={inp} value={q.label} maxLength={300} placeholder="z. B. Welche Leistung wird benötigt?" onChange={(e) => patch({ label: e.target.value })} autoFocus={!q.label} />
      </Field>
      <Field label="Antworttyp">
        <select
          className={inp}
          value={t}
          onChange={(e) => {
            const nt = e.target.value as FormQuestionType;
            const fresh = newQuestion(nt);
            set((x) => ({ ...fresh, id: x.id, label: x.label, help: x.help, required: x.required, visibleIf: x.visibleIf, requiredIf: x.requiredIf, options: (nt === 'choice' || nt === 'multichoice') && x.options?.length ? x.options : fresh.options }));
          }}
        >
          {FORM_QUESTION_TYPES.map((x) => (
            <option key={x} value={x}>
              {FORM_QUESTION_LABELS[x]}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t === 'info' ? 'Text' : 'Hilfetext'}>
        <textarea className={cx(inp, 'min-h-16 py-2')} value={q.help ?? ''} maxLength={2000} onChange={(e) => patch({ help: e.target.value || undefined })} />
      </Field>
      {(t === 'text' || t === 'longtext' || t === 'email' || t === 'phone' || t === 'url') && (
        <Field label="Platzhalter" hint="Beispiel im leeren Feld – ersetzt nie die Beschriftung.">
          <input className={inp} value={q.placeholder ?? ''} maxLength={200} onChange={(e) => patch({ placeholder: e.target.value || undefined })} />
        </Field>
      )}
      {t !== 'info' && t !== 'calc' && (
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <input type="checkbox" className="size-5" checked={!!q.required} onChange={(e) => patch({ required: e.target.checked || undefined })} />
          {t === 'confirm' ? 'Muss angehakt werden' : 'Pflichtfrage'}
        </label>
      )}

      {(t === 'text' || t === 'longtext') && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Mindestlänge">
            <input className={inp} inputMode="numeric" value={q.minLength ?? ''} onChange={(e) => patch({ minLength: num(e.target.value) })} />
          </Field>
          <Field label="Höchstlänge">
            <input className={inp} inputMode="numeric" value={q.maxLength ?? ''} placeholder={t === 'text' ? '300' : '5000'} onChange={(e) => patch({ maxLength: num(e.target.value) })} />
          </Field>
        </div>
      )}
      {(t === 'number' || t === 'currency') && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Minimum">
            <input className={inp} inputMode="decimal" value={q.min ?? ''} onChange={(e) => patch({ min: num(e.target.value) })} />
          </Field>
          <Field label="Maximum">
            <input className={inp} inputMode="decimal" value={q.max ?? ''} onChange={(e) => patch({ max: num(e.target.value) })} />
          </Field>
          {t === 'number' && (
            <>
              <Field label="Nachkommastellen">
                <input className={inp} inputMode="numeric" value={q.decimals ?? 0} onChange={(e) => patch({ decimals: Math.max(0, Math.min(6, Number(e.target.value) || 0)) })} />
              </Field>
              <Field label="Einheit">
                <input className={inp} value={q.unit ?? ''} maxLength={20} placeholder="z. B. Stück" onChange={(e) => patch({ unit: e.target.value || undefined })} />
              </Field>
            </>
          )}
        </div>
      )}
      {(t === 'choice' || t === 'multichoice') && <OptionsEditor def={def} q={q} refs={refs} patch={patch} />}
      {(t === 'date' || t === 'datetime') && (
        <Field label="Darf nicht vor … liegen" hint="Feldvergleich, z. B. Ende nicht vor Beginn.">
          <select className={inp} value={q.notBefore ?? ''} onChange={(e) => patch({ notBefore: e.target.value || undefined })}>
            <option value="">–</option>
            {refs
              .filter((r) => r.type === 'date' || r.type === 'datetime')
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
          </select>
        </Field>
      )}
      {t === 'file' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Höchstens Dateien">
              <input className={inp} inputMode="numeric" value={q.maxFiles ?? 3} onChange={(e) => patch({ maxFiles: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })} />
            </Field>
            <Field label="MB je Datei">
              <input className={inp} inputMode="numeric" value={q.maxSizeMb ?? 10} onChange={(e) => patch({ maxSizeMb: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })} />
            </Field>
          </div>
          <fieldset className="flex flex-wrap gap-3 text-sm">
            <legend className="mb-1 font-medium">Erlaubte Dateien</legend>
            {(['image', 'pdf', 'text'] as const).map((a) => (
              <label key={a} className="flex min-h-10 items-center gap-2">
                <input
                  type="checkbox"
                  checked={(q.accept ?? []).includes(a)}
                  onChange={(e) => {
                    const cur = q.accept ?? [];
                    const n = e.target.checked ? [...cur, a] : cur.filter((x) => x !== a);
                    if (n.length) patch({ accept: n });
                  }}
                />
                {a === 'image' ? 'Bilder (JPEG, PNG, WebP)' : a === 'pdf' ? 'PDF' : 'Text'}
              </label>
            ))}
          </fieldset>
        </>
      )}
      {t === 'rating' && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="1 bedeutet">
            <input className={inp} value={q.lowLabel ?? ''} maxLength={60} placeholder="sehr unzufrieden" onChange={(e) => patch({ lowLabel: e.target.value || undefined })} />
          </Field>
          <Field label="5 bedeutet">
            <input className={inp} value={q.highLabel ?? ''} maxLength={60} placeholder="sehr zufrieden" onChange={(e) => patch({ highLabel: e.target.value || undefined })} />
          </Field>
        </div>
      )}
      {t === 'calc' && <FormulaEditor def={def} q={q} patch={patch} />}

      {t !== 'info' && (
        <details open={!!(q.visibleIf?.items.length || q.requiredIf?.items.length)} className="text-sm">
          <summary className="cursor-pointer font-medium">Erweitert: Bedingungen</summary>
          <div className="mt-2 flex flex-col gap-3">
            {section?.repeat && <p className="text-xs text-[var(--ink-soft)]">In wiederholten Einträgen beziehen sich Bedingungen auf frühere Felder desselben Eintrags oder auf Fragen davor.</p>}
            <div className="flex flex-col gap-1">
              <span className="font-medium">Anzeigen, wenn …</span>
              <ConditionEditor def={def} refs={refs} value={q.visibleIf} onChange={(c) => patch({ visibleIf: c })} label="Bedingung zum Anzeigen" />
            </div>
            {t !== 'calc' && (
              <div className="flex flex-col gap-1">
                <span className="font-medium">Pflicht, wenn sichtbar und …</span>
                {q.required ? (
                  <p className="text-xs text-[var(--ink-soft)]">Die Frage ist immer Pflicht, sobald sie sichtbar ist.</p>
                ) : (
                  <ConditionEditor def={def} refs={refs} value={q.requiredIf} onChange={(c) => patch({ requiredIf: c })} label="Bedingung für Pflicht" />
                )}
              </div>
            )}
          </div>
        </details>
      )}
      <DeleteBtn onDelete={onDelete} what={`Frage „${q.label || 'ohne Beschriftung'}“`} />
    </div>
  );
}

function OptionsEditor({ def, q, refs, patch }: { def: Def; q: FormQuestion; refs: ReturnType<typeof refsBefore>; patch: (p: Partial<FormQuestion>) => void }) {
  const opts = q.options ?? [];
  const setOpts = (o: typeof opts) => patch({ options: o });
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium">Antworten</legend>
      {opts.map((o, i) => (
        <div key={o.id} className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <input className={inp} aria-label={`Antwort ${i + 1}`} value={o.label} maxLength={200} onChange={(e) => setOpts(opts.map((x) => (x.id === o.id ? { ...x, label: e.target.value } : x)))} />
            <button type="button" className="inline-flex size-10 shrink-0 items-center justify-center rounded-md disabled:opacity-30" aria-label={`Antwort ${i + 1} nach oben`} disabled={i === 0} onClick={() => setOpts(swap(opts, i, i - 1))}>
              <ArrowUp size={14} />
            </button>
            <button type="button" className="inline-flex size-10 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:text-rose-600 disabled:opacity-30" aria-label={`Antwort ${i + 1} entfernen`} disabled={opts.length <= 1} onClick={() => setOpts(opts.filter((x) => x.id !== o.id))}>
              <Trash2 size={14} />
            </button>
          </div>
          {refs.length > 0 && (
            <details open={!!o.visibleIf?.items.length} className="ml-1 text-xs">
              <summary className="cursor-pointer text-[var(--ink-soft)]">nur anbieten, wenn …</summary>
              <div className="mt-1">
                <ConditionEditor def={def} refs={refs} value={o.visibleIf} onChange={(c) => setOpts(opts.map((x) => (x.id === o.id ? { ...x, visibleIf: c } : x)))} label="Bedingung" />
              </div>
            </details>
          )}
        </div>
      ))}
      <Button type="button" size="sm" variant="ghost" className="self-start" disabled={opts.length >= 50} onClick={() => setOpts([...opts, { id: formId('o'), label: `Antwort ${opts.length + 1}` }])}>
        <Plus size={14} /> Antwort
      </Button>
      {q.type === 'choice' ? (
        <Field label="Darstellung">
          <select className={inp} value={q.display ?? 'auto'} onChange={(e) => patch({ display: e.target.value as 'auto' })}>
            <option value="auto">Automatisch (bis 6 Antworten als Knöpfe)</option>
            <option value="radio">Knöpfe</option>
            <option value="select">Auswahlliste</option>
          </select>
        </Field>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Mindestens">
            <input className={inp} inputMode="numeric" value={q.minSelect ?? ''} onChange={(e) => patch({ minSelect: num(e.target.value) })} />
          </Field>
          <Field label="Höchstens">
            <input className={inp} inputMode="numeric" value={q.maxSelect ?? ''} onChange={(e) => patch({ maxSelect: num(e.target.value) })} />
          </Field>
        </div>
      )}
    </fieldset>
  );
}

function FormulaEditor({ def, q, patch }: { def: Def; q: FormQuestion; patch: (p: Partial<FormQuestion>) => void }) {
  const { refs, sums } = formulaRefs(def, q.id);
  const qs = allQuestions(def);
  const label = (id: string) => qs.find((x) => x.id === id)?.label ?? '?';
  const [text, setText] = useState(() => formatExpr(q.formula, label));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setText(formatExpr(q.formula, label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.id]);
  const apply = (s: string) => {
    setText(s);
    if (!s.trim()) return setError('Bitte eine Formel eingeben.');
    try {
      const e = parseFormula(s, (l) => [...refs, ...sums].find((r) => r.label.trim().toLowerCase() === l.trim().toLowerCase())?.id ?? null);
      setError(null);
      patch({ formula: e });
    } catch (e) {
      setError(e instanceof FormulaError ? e.message : 'Ungültige Formel');
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <Field label="Formel" hint="Fragen in eckigen Klammern, Rechenzeichen + − × ÷ und Klammern. Summe über Einträge: SUMME([Feld]).">
        <input className={cx(inp, 'font-mono')} value={text} onChange={(e) => apply(e.target.value)} aria-invalid={!!error} />
      </Field>
      {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
      {(refs.length > 0 || sums.length > 0) && (
        <div className="flex flex-wrap gap-1 text-xs">
          {refs.map((r) => (
            <button key={r.id} type="button" className="min-h-8 rounded-md bg-black/5 px-2 dark:bg-white/10" onClick={() => apply(`${text}${text && !/[\s(]$/.test(text) ? ' ' : ''}[${r.label}]`)}>
              [{r.label}]
            </button>
          ))}
          {sums.map((r) => (
            <button key={r.id} type="button" className="min-h-8 rounded-md bg-sky-100 px-2 dark:bg-sky-900" onClick={() => apply(`${text}${text ? ' ' : ''}SUMME([${r.label}])`)}>
              SUMME([{r.label}])
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Nachkommastellen" hint="Kaufmännisch gerundet">
          <input className={inp} inputMode="numeric" value={q.decimals ?? 2} onChange={(e) => patch({ decimals: Math.max(0, Math.min(6, Number(e.target.value) || 0)) })} />
        </Field>
        <Field label="Einheit">
          <input className={inp} value={q.unit ?? ''} maxLength={20} placeholder="z. B. €" onChange={(e) => patch({ unit: e.target.value || undefined })} />
        </Field>
      </div>
    </div>
  );
}
